import ReactMarkdown from 'react-markdown';
import { Box, Link, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'wouter';

interface ChatMessageContentProps {
  content: string;
}

/**
 * Renders assistant message content as formatted markdown.
 * Handles bold, italic, lists, paragraphs, and recipe:// links.
 */
export default function ChatMessageContent({ content }: ChatMessageContentProps) {
  return (
    <ReactMarkdown
      urlTransform={(url) => url}
      components={{
        p: ({ children }) => (
          <Text
            as="p"
            fontSize="sm"
            lineHeight="tall"
            color="text.primary"
            fontFamily="body"
            mb={2}
            _last={{ mb: 0 }}
          >
            {children}
          </Text>
        ),
        strong: ({ children }) => (
          <Box as="strong" fontWeight="600" display="inline">
            {children}
          </Box>
        ),
        em: ({ children }) => (
          <Box as="em" fontStyle="italic" display="inline">
            {children}
          </Box>
        ),
        ol: ({ children }) => (
          <Box as="ol" listStyleType="decimal" pl={5} mb={2} _last={{ mb: 0 }}>
            {children}
          </Box>
        ),
        ul: ({ children }) => (
          <Box as="ul" listStyleType="disc" pl={5} mb={2} _last={{ mb: 0 }}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Box
            as="li"
            fontSize="sm"
            lineHeight="tall"
            color="text.primary"
            fontFamily="body"
            mb={1}
            _last={{ mb: 0 }}
          >
            {children}
          </Box>
        ),
        a: ({ href, children }) => {
          const recipeMatch = href?.match(/^recipe:\/\/([a-fA-F0-9-]{36})$/);
          if (recipeMatch) {
            return (
              <Link
                as={RouterLink}
                href={`/recipes/${recipeMatch[1]}`}
                color="accent.emphasis"
                textDecoration="underline"
                fontWeight="medium"
                _hover={{ color: 'accent.emphasisHover', textDecoration: 'none' }}
              >
                {children}
              </Link>
            );
          }
          const collectionMatch = href?.match(/^collection:\/\/([a-fA-F0-9-]{36})$/);
          if (collectionMatch) {
            return (
              <Link
                as={RouterLink}
                href={`/recipe-groups/${collectionMatch[1]}`}
                color="accent.emphasis"
                textDecoration="underline"
                fontWeight="medium"
                _hover={{ color: 'accent.emphasisHover', textDecoration: 'none' }}
              >
                {children}
              </Link>
            );
          }
          return (
            <Link
              href={href}
              color="accent.emphasis"
              textDecoration="underline"
            >
              {children}
            </Link>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
